import { PayCommissionDefaultRateType, RateType } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import * as yup from 'yup';

import { getRateTypeLabel } from '~pages/Settings/RatesManagement/rateUtils';
import { toDecimal } from '~utils/decimal';

const rateTypeByCommissionRateType = {
  [PayCommissionDefaultRateType.RATE_FLAT_RATE]: RateType.RATE_FLAT_RATE,
  [PayCommissionDefaultRateType.RATE_PER_BUSHEL]: RateType.RATE_PER_BUSHEL,
  [PayCommissionDefaultRateType.RATE_PER_HOUR]: RateType.RATE_PER_HOUR,
  [PayCommissionDefaultRateType.RATE_PER_LOAD]: RateType.RATE_PER_LOAD,
  [PayCommissionDefaultRateType.RATE_PER_POUND]: RateType.RATE_PER_POUND,
  [PayCommissionDefaultRateType.RATE_PER_TON]: RateType.RATE_PER_TON,
  [PayCommissionDefaultRateType.RATE_PER_TONNE]: RateType.RATE_PER_TONNE,
  [PayCommissionDefaultRateType.RATE_PER_YARD]: RateType.RATE_PER_YARD,
} satisfies Record<PayCommissionDefaultRateType, RateType>;

export const commissionRateTypeLabel = (type: PayCommissionDefaultRateType) =>
  getRateTypeLabel(rateTypeByCommissionRateType[type]);

// the API stores the percentage as decimal(7,4)
export const MAX_COMMISSION_PERCENTAGE = 100;
export const COMMISSION_PERCENTAGE_DECIMAL_PLACES = 4;
const commissionPercentagePattern = new RegExp(
  `^\\d+(\\.\\d{1,${COMMISSION_PERCENTAGE_DECIMAL_PLACES}})?$`,
);

export const commissionDefaultSchema = yup.object({
  payee: yup
    .object({
      label: yup.string().required(),
      value: yup.string().required(),
    })
    .nullable()
    .required(t('error_messages.pay_commission_defaults.payee_required')),
  percentage: yup
    .string()
    .required(t('error_messages.pay_commission_defaults.percentage_required'))
    .test(
      'commission-percentage',
      t('error_messages.pay_commission_defaults.percentage_invalid'),
      (value) => {
        if (!value || !commissionPercentagePattern.test(value)) return false;
        const percentage = toDecimal(value);
        return (
          percentage.greaterThanOrEqualTo(0) &&
          percentage.lessThanOrEqualTo(MAX_COMMISSION_PERCENTAGE)
        );
      },
    ),
  rateType: yup
    .object({
      label: yup.string().required(),
      value: yup
        .mixed<PayCommissionDefaultRateType>()
        .oneOf(Object.values(PayCommissionDefaultRateType))
        .required(),
    })
    .nullable()
    .required(t('error_messages.pay_commission_defaults.rate_type_required')),
});

export type CommissionDefaultFormData = yup.InferType<typeof commissionDefaultSchema>;

export type CommissionDefaultFormValues = {
  [Field in keyof CommissionDefaultFormData]: CommissionDefaultFormData[Field] | null;
};

export type CommissionDefaultDtoAsyncError = {
  field: keyof CommissionDefaultFormData;
  message: string;
};
